import type { SearchSuggestion } from '@/api/v4/search.api';
import { useFileSuggestionsTableConfig } from './use-file-suggestions-table-config';
import { TableHeaderCell } from '@/ui/table/infinite-table/table-header-cell';
import { TableBody } from '@/ui/table/infinite-table/table-body';
import { colors } from '@/theme/colors';
import { styled } from 'goober';
import { ScrollableTableContainer } from '../shared/scrollable-table-container';
import { Stack } from '@/ui/line/line';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { useTranslation } from 'react-i18next';

export type ProjectSearchSuggestion = {
  basicData: SearchSuggestion;
};

export const FileSuggestionsTable = ({
  data,
  isLoading = false,
  hasNextPage,
  onShowMore,
}: {
  isLoading?: boolean;
  data: SearchSuggestion[];
  hasNextPage?: boolean;
  onShowMore?: () => void;
}) => {
  const { t } = useTranslation('default', { keyPrefix: 'globalSearch' });
  const { rows, headers } = useFileSuggestionsTableConfig(data);

  if (isLoading) {
    return null;
  }

  return (
    <Stack>
      <ScrollableTableContainer>
        <Table>
          <thead>
            <tr>
              {headers.cells.map(cell => (
                <TableHeaderCell key={cell.columnKey} cell={cell} />
              ))}
            </tr>
          </thead>
          <TableBody
            id="file-suggestions-table-body"
            rows={rows}
            isLoading={isLoading}
            borderColor={colors.gray.c3}
          />
          {!isLoading && hasNextPage ? (
            <tfoot>
              <tr>
                <td colSpan={headers.cells.length}>
                  <Flexbox justify="center" margin="12px">
                    <Button variant="secondary" onClick={onShowMore}>
                      {t('showMore')}
                    </Button>
                  </Flexbox>
                </td>
              </tr>
            </tfoot>
          ) : undefined}
        </Table>
      </ScrollableTableContainer>
    </Stack>
  );
};

const Table = styled('table')`
  ${({ theme }) => theme.mixins.scrollbar};
  border-collapse: separate;
  overflow: auto;
  display: table;
  max-height: 100%;
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.suggestionsTable};
  min-width: 100%;
  padding: 0px;
`;

// Uses shared ScrollableTableContainer
